import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import axios from '../../axiosConfig';
import Shell from '../dashboard/Shell';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { navigateToUpgrade } from '../subscription/upgradeNav';
import ChartJSLine from '../../components/charts/ChartJSLine';
import ChartJSBar from '../../components/charts/ChartJSBar';
import ChartJSPie from '../../components/charts/ChartJSPie';
import './ShiftAnalytics.css';

const currency = new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' });

const MetricCard = ({ title, value, subtitle, icon, trend, color = '#0D7463' }) => (
  <div className="shift-metric-card">
    <div className="shift-metric-icon" style={{ backgroundColor: `${color}15`, color }}>
      {icon}
    </div>
    <div className="shift-metric-info">
      <p className="shift-metric-title">{title}</p>
      <h3 className="shift-metric-value">{value}</h3>
      {subtitle && <p className="shift-metric-subtitle">{subtitle}</p>}
      {trend !== undefined && (
        <div className={`shift-metric-trend ${trend >= 0 ? 'positive' : 'negative'}`}>
          {trend >= 0 ? '+' : ''}{trend.toFixed(1)}%
        </div>
      )}
    </div>
  </div>
);

const ChartCard = ({ title, children, className = '' }) => (
  <div className={`shift-chart-card ${className}`}>
    <div className="shift-chart-header">
      <h3 className="shift-chart-title">{title}</h3>
    </div>
    <div className="shift-chart-content">
      {children}
    </div>
  </div>
);

const ShiftAnalytics = () => {
  const { user } = useSecureAuth();
  const navigate = useNavigate();
  const { hasAccess, loading: featureLoading } = useFeatureAccess('analytics.shift');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresUpgrade, setRequiresUpgrade] = useState(false);
  const [data, setData] = useState(null);

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedEmployee, setSelectedEmployee] = useState('');

  // Active tab
  const [activeTab, setActiveTab] = useState('overview');

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setRequiresUpgrade(false);

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (selectedEmployee) params.append('employeeId', selectedEmployee);

      const response = await axios.get(`/pos/shift-analytics?${params.toString()}`);

      if (response.data.success) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch shift analytics:', err);
      if (err.response?.data?.requiresUpgrade || err.response?.data?.data?.upgradeRequired || err.response?.status === 403) {
        setRequiresUpgrade(true);
        setError(err.response?.data?.message || err.response?.data?.error || 'This feature requires a premium subscription');
      } else {
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load shift analytics');
      }
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedEmployee]);

  useEffect(() => {
    if (hasAccess) {
      fetchAnalytics();
    }
  }, [fetchAnalytics, hasAccess]);

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-NG', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Prepare chart data in the correct format: [{ label, value }]
  const salesTrendData = data?.charts?.salesTrend?.length > 0
    ? data.charts.salesTrend.map(d => ({
        label: new Date(d.date).toLocaleDateString('en-NG', { month: 'short', day: 'numeric' }),
        value: d.sales || 0
      }))
    : null;

  const salesByDayData = data?.charts?.salesByDay?.some(d => d.sales > 0)
    ? data.charts.salesByDay.map(d => ({
        label: d.day.slice(0, 3),
        value: d.sales || 0
      }))
    : null;

  const salesByHourData = data?.charts?.salesByHour?.some(d => d.sales > 0)
    ? data.charts.salesByHour.filter(d => d.sales > 0).map(d => ({
        label: `${d.hour}:00`,
        value: d.sales || 0
      }))
    : null;

  const paymentMethodsData = data?.paymentMethods?.length > 0
    ? data.paymentMethods.map(p => ({
        label: p.method,
        value: p.total || 0
      }))
    : null;

  const employeeTipsData = data?.employeePerformance?.length > 0
    ? data.employeePerformance.filter(e => e.tips > 0).map(e => ({
        label: e.name?.length > 12 ? e.name.slice(0, 12) + '...' : (e.name || 'Unknown'),
        value: e.tips || 0
      }))
    : null;

  const topProductsData = data?.topProducts?.length > 0
    ? data.topProducts.slice(0, 8).map(p => ({
        label: p.name?.length > 12 ? p.name.slice(0, 12) + '...' : (p.name || 'Unknown'),
        value: p.quantity || 0
      }))
    : null;

  // Show loading state while checking feature access
  if (featureLoading) {
    return (
      <Shell>
        <div className="shift-analytics-container">
          <div className="shift-analytics-loading">
            <div className="loading-spinner"></div>
            <p>Checking access...</p>
          </div>
        </div>
      </Shell>
    );
  }

  // Show upgrade prompt if no feature access
  if (!hasAccess || requiresUpgrade) {
    return (
      <Shell>
        <div className="shift-analytics-container">
          <div className="shift-analytics-upgrade">
            <div className="upgrade-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#0D7463" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <h2>Upgrade to unlock</h2>
            <p>Shift Analytics needs PRO or PREMIUM. You'll get sales-by-shift, employee tip splits, and per-method payment breakdowns.</p>
            <button className="upgrade-btn" onClick={() => navigateToUpgrade(navigate)}>
              See plans
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="shift-analytics-container">
        {/* Header */}
        <div className="shift-analytics-header">
          <div>
            <h1>Shift Analytics</h1>
            <p>Sales, tips, and shift performance.</p>
          </div>
          <button className="refresh-btn" onClick={fetchAnalytics} disabled={loading}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6"/>
              <path d="M1 20v-6h6"/>
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
            </svg>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {/* Filters */}
        <div className="shift-analytics-filters">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Employee</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option value="">All Employees</option>
              {data?.employees?.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.name} ({emp.role || 'Team Member'})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="shift-analytics-tabs">
          <button
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab-btn ${activeTab === 'employees' ? 'active' : ''}`}
            onClick={() => setActiveTab('employees')}
          >
            Employee Performance
          </button>
          <button
            className={`tab-btn ${activeTab === 'shifts' ? 'active' : ''}`}
            onClick={() => setActiveTab('shifts')}
          >
            Shift Details
          </button>
          <button
            className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Top Products
          </button>
        </div>

        {loading ? (
          <div className="shift-analytics-loading">
            <div className="loading-spinner"></div>
            <p>Loading analytics...</p>
          </div>
        ) : error && !requiresUpgrade ? (
          <div className="shift-analytics-error">
            <p>{error}</p>
            <button onClick={fetchAnalytics}>Try Again</button>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="shift-analytics-content">
                {/* Summary Metrics */}
                <div className="shift-metrics-grid">
                  <MetricCard
                    title="Total Sales"
                    value={currency.format(data?.summary?.totalSales || 0)}
                    subtitle={`${data?.summary?.totalOrders || 0} orders`}
                    icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>}
                    color="#0D7463"
                  />
                  <MetricCard
                    title="Total Tips"
                    value={currency.format(data?.summary?.totalTips || 0)}
                    subtitle="Collected by team"
                    icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>}
                    color="#F59E0B"
                  />
                  <MetricCard
                    title="Avg Order Value"
                    value={currency.format(data?.summary?.avgOrderValue || 0)}
                    subtitle="Per transaction"
                    icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>}
                    color="#3B82F6"
                  />
                  <MetricCard
                    title="Total Shifts"
                    value={data?.summary?.totalShifts || 0}
                    subtitle={`${data?.summary?.completedShifts || 0} completed, ${data?.summary?.activeShifts || 0} active`}
                    icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
                    color="#8B5CF6"
                  />
                  <MetricCard
                    title="Cash Difference"
                    value={currency.format(data?.summary?.totalCashDifference || 0)}
                    subtitle={`Avg: ${currency.format(data?.summary?.avgCashDifference || 0)}`}
                    icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>}
                    color={(data?.summary?.totalCashDifference || 0) >= 0 ? '#10B981' : '#EF4444'}
                  />
                </div>

                {/* Charts */}
                <div className="shift-charts-grid">
                  <ChartCard title="Sales Trend" className="full-width">
                    {salesTrendData ? (
                      <ChartJSLine data={salesTrendData} height="280px" color="#0D7463" title="Sales" isCurrency={true} />
                    ) : (
                      <div className="no-chart-data">No sales data available for this period</div>
                    )}
                  </ChartCard>

                  <ChartCard title="Payment Methods">
                    {paymentMethodsData ? (
                      <ChartJSPie data={paymentMethodsData} height="280px" title="Payment Methods" isCurrency={true} />
                    ) : (
                      <div className="no-chart-data">No payment data available</div>
                    )}
                  </ChartCard>

                  <ChartCard title="Sales by Day of Week">
                    {salesByDayData ? (
                      <ChartJSBar data={salesByDayData} height="280px" color="#0D7463" title="Sales" isCurrency={true} />
                    ) : (
                      <div className="no-chart-data">No daily sales data available</div>
                    )}
                  </ChartCard>

                  <ChartCard title="Peak Hours">
                    {salesByHourData ? (
                      <ChartJSBar data={salesByHourData} height="280px" color="#3B82F6" title="Sales" isCurrency={true} />
                    ) : (
                      <div className="no-chart-data">No hourly data available</div>
                    )}
                  </ChartCard>
                </div>
              </div>
            )}

            {/* Employee Performance Tab */}
            {activeTab === 'employees' && (
              <div className="shift-analytics-content">
                <div className="employees-section">
                  <div className="employees-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th className="text-right">Orders</th>
                          <th className="text-right">Sales</th>
                          <th className="text-right">Tips</th>
                          <th className="text-right">Avg Order</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data?.employeePerformance?.length > 0 ? (
                          data.employeePerformance.map((emp, i) => (
                            <tr key={emp.id || i}>
                              <td>
                                <div className="employee-cell">
                                  <div className="employee-avatar" style={{ backgroundColor: ['#0D7463', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444'][i % 5] }}>
                                    {(emp.name || 'U').charAt(0).toUpperCase()}
                                  </div>
                                  <span className="employee-name-text">{emp.name || 'Unassigned'}</span>
                                </div>
                              </td>
                              <td className="text-right">{emp.orders}</td>
                              <td className="text-right">{currency.format(emp.sales)}</td>
                              <td className="text-right">{currency.format(emp.tips)}</td>
                              <td className="text-right">{currency.format(emp.avgOrderValue)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="no-data-cell">No employee performance data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {employeeTipsData && (
                    <ChartCard title="Tips by Employee" className="employee-chart">
                      <ChartJSBar data={employeeTipsData} height="300px" color="#F59E0B" title="Tips" isCurrency={true} />
                    </ChartCard>
                  )}
                </div>
              </div>
            )}

            {/* Shift Details Tab */}
            {activeTab === 'shifts' && (
              <div className="shift-analytics-content">
                <div className="shifts-table-container">
                  <table className="data-table shifts-table">
                    <thead>
                      <tr>
                        <th>Started</th>
                        <th>Ended</th>
                        <th className="text-right">Duration</th>
                        <th className="text-right">Opening</th>
                        <th className="text-right">Closing</th>
                        <th className="text-right">Difference</th>
                        <th className="text-right">Orders</th>
                        <th className="text-right">Sales</th>
                        <th className="text-right">Tips</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.shifts?.length > 0 ? (
                        data.shifts.map((shift, i) => (
                          <tr key={shift.id || i}>
                            <td className="date-cell">{formatDate(shift.startedAt)}</td>
                            <td className="date-cell">{shift.endedAt ? formatDate(shift.endedAt) : '-'}</td>
                            <td className="text-right">{formatDuration(shift.duration)}</td>
                            <td className="text-right">{currency.format(shift.openingCash)}</td>
                            <td className="text-right">{shift.closingCash !== null ? currency.format(shift.closingCash) : '-'}</td>
                            <td className={`text-right ${(shift.cashDifference || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                              {shift.cashDifference !== null ? currency.format(shift.cashDifference) : '-'}
                            </td>
                            <td className="text-right">{shift.calculatedOrders || shift.totalOrders || 0}</td>
                            <td className="text-right">{currency.format(shift.calculatedSales || shift.totalSales || 0)}</td>
                            <td className="text-right">{currency.format(shift.tips || 0)}</td>
                            <td className="text-center">
                              <span className={`status-badge status-${(shift.status || 'unknown').toLowerCase()}`}>
                                {shift.status || 'Unknown'}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="10" className="no-data-cell">No shifts found for this period</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top Products Tab */}
            {activeTab === 'products' && (
              <div className="shift-analytics-content">
                <div className="products-section">
                  {topProductsData ? (
                    <ChartCard title="Top Products by Quantity Sold" className="products-chart">
                      <ChartJSBar data={topProductsData} height="350px" color="#0D7463" title="Quantity" />
                    </ChartCard>
                  ) : (
                    <div className="no-chart-data">No product data available</div>
                  )}

                  <div className="products-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Product</th>
                          <th className="text-right">Quantity Sold</th>
                          <th className="text-right">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data?.topProducts?.length > 0 ? (
                          data.topProducts.map((product, i) => (
                            <tr key={product.id || i}>
                              <td>
                                <span className={`rank-badge rank-${i < 3 ? i + 1 : 'other'}`}>#{i + 1}</span>
                              </td>
                              <td className="product-name-cell">{product.name || 'Unknown Product'}</td>
                              <td className="text-right">{product.quantity}</td>
                              <td className="text-right">{currency.format(product.revenue)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="no-data-cell">No product data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
};

export default ShiftAnalytics;
